import React, { useState, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { NotesFormParams } from './useNotesQueries';
import styles from './NotesDialog.module.scss';
import Note, { ClaimFocusProps } from './Note';
import { Note as NoteType } from 'pages/StudyList/StudyList';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  TextArea,
  FormControl,
  FormLabel,
  Loading,
  Message,
  Text,
  VisuallyHidden,
} from 'components/core';
import { Inline, Stack } from 'components/layout';
import { useCurrentReviewer } from 'hooks/reviewers/useCurrentReviewer';

const ADD_NOTE_FORM_ID = 'add-note-form';

export type NotesDialogProps = {
  title: string | null;
  onClose?: () => void;
  noteCategory: string;
  onSubmit: (arg: NotesFormParams) => void;
  isFetching: boolean;
  isCreating: boolean;
  fetchError?: unknown;
  mutationError?: unknown;
  notes: NoteType[];
  invalidateQueries: () => Promise<void>;
};

const NotesDialog = ({
  onClose,
  title,
  noteCategory,
  onSubmit,
  isFetching,
  notes,
  fetchError,
  mutationError,
  isCreating,
  invalidateQueries,
}: NotesDialogProps) => {
  const [isOpen, setOpen] = useState(true);
  const [focusedNote, setFocusedNote] = useState<number | null>(null);

  const form = useForm<NotesFormParams>({
    mode: 'onSubmit',
  });
  const {
    handleSubmit,
    reset,
    setFocus,
    register,
    formState: { isSubmitSuccessful },
  } = form;
  const {
    currentReviewer,
    isLoading: isCurrentReviewerLoading,
  } = useCurrentReviewer();
  const isActionInProgress = isCreating || (isSubmitSuccessful && isFetching);
  const screenReaderConfirmationMessage =
    isSubmitSuccessful && !isActionInProgress && !mutationError
      ? 'Note added'
      : null;

  const claimFocus = ({ id, idAfter }: ClaimFocusProps) => {
    if (id) {
      setFocusedNote(id);
    } else if (idAfter) {
      const sortedIds = notes.map((note) => note.id).sort((a, b) => a - b);
      const closestId = sortedIds.find((id) => id > idAfter);
      if (closestId) {
        setFocusedNote(closestId);
      }
    }
  };

  useEffect(() => {
    setFocus('note');
  }, [form]);

  const isLoading = (isFetching || isCurrentReviewerLoading) && !notes;
  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalHeader>Notes</ModalHeader>
      <ModalBody>
        {/* Only show this spinner on initial load - for other actions the loading state is in the action button */}
        {isLoading ? (
          <Inline>
            <Loading size="lg" />
          </Inline>
        ) : (
          <Stack spacing={4}>
            {title && (
              <ModalBody.Heading className={styles.Title}>
                {title}
              </ModalBody.Heading>
            )}
            {!!notes?.length && (
              <Stack component="ul" spacing={5}>
                {notes
                  .sort(
                    (a, b) =>
                      Date.parse(a.created_at) - Date.parse(b.created_at)
                  )
                  .map((note: NoteType) => (
                    <Note
                      key={note.id}
                      note={note}
                      currentReviewer={currentReviewer}
                      onChange={invalidateQueries}
                      claimFocus={claimFocus}
                      isFocused={focusedNote === note.id}
                    />
                  ))}
              </Stack>
            )}
            <FormProvider {...form}>
              <form
                id={ADD_NOTE_FORM_ID}
                onSubmit={handleSubmit(async ({ note }: NotesFormParams) => {
                  onSubmit({ note, category: noteCategory });
                  reset();
                })}
                noValidate // Suppress browser default validations as the form is using custom validations
              >
                <Stack spacing={3} className={styles.FooterStack}>
                  <FormControl
                    error={form.formState.errors.note?.message}
                    className={styles.NoteInput}
                  >
                    <FormLabel>Note</FormLabel>
                    <TextArea
                      placeholder="Add your note to the team here..."
                      {...register('note', {
                        required: 'Note cannot be empty',
                      })}
                      required
                    />
                  </FormControl>

                  {(fetchError || mutationError) && (
                    <Message type="danger" variant="prominent" role="alert">
                      <Text>
                        {fetchError &&
                          'Error fetching notes. Please try refreshing the page.'}
                        {mutationError &&
                          'Error saving changes. Please try again or refresh the page.'}
                      </Text>
                    </Message>
                  )}
                </Stack>
                <VisuallyHidden
                  component="div"
                  aria-live="polite"
                  role="status"
                >
                  {screenReaderConfirmationMessage}
                </VisuallyHidden>
              </form>
            </FormProvider>
          </Stack>
        )}
      </ModalBody>
      {!isLoading && (
        <ModalFooter>
          <Inline alignX="right" spacing={3}>
            <Button
              htmlType="button"
              size="sm"
              type="neutral"
              variant="outline"
              disabled={isActionInProgress}
              onClick={() => {
                onClose?.();
                setOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button
              htmlType="submit"
              form={ADD_NOTE_FORM_ID}
              size="sm"
              type="brand"
              isLoading={isActionInProgress}
              disabled={isActionInProgress}
            >
              Add note
            </Button>
          </Inline>
        </ModalFooter>
      )}
    </Modal>
  );
};

export default NotesDialog;
